/* MFC • Product Info + Accordion
   - Smooth expand/collapse animation
   - Uses GSAP if available, falls back to height tween with requestAnimationFrame
   - Respects prefers-reduced-motion (config + media query)
   - Close-others option and initial-open index supported
*/

(function () {
  if (typeof window === 'undefined') return;

  const prefersReduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(section) {
    const config = safeParse(section.getAttribute('data-mfc-accordion')) || {};
    const honorReduced = !!config.reducedMotion;
    const closeOthers = !!config.closeOthers;
    const initialOpen = Number.isInteger(config.initialOpenIndex)
      ? config.initialOpenIndex
      : -1;

    const triggers = Array.from(
      section.querySelectorAll('.mfc-acc__trigger')
    );
    const panels = Array.from(section.querySelectorAll('.mfc-acc__panel'));
    const duration = clamp(parseFloat(getCssVar(section, '--mfc-ac-duration')) || 0.35, 0.05, 2);

    // Ensure closed initial state
    panels.forEach((p) => setHeight(p, 0));

    // Initial open
    if (initialOpen >= 0 && triggers[initialOpen]) {
      open(triggers[initialOpen], panels[initialOpen], { instant: honorReduced && prefersReduced });
    }

    // Events
    triggers.forEach((btn, i) => {
      const panel = panels[i];
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          close(btn, panel);
        } else {
          if (closeOthers) {
            triggers.forEach((b, j) => {
              if (j !== i) close(b, panels[j], { silent: true });
            });
          }
          open(btn, panel);
        }
      });

      // Keyboard: Enter/Space already handled by button
    });

    // Helpers
    function open(btn, panel, opts = {}) {
      btn.setAttribute('aria-expanded', 'true');
      const target = panel.querySelector('.mfc-acc__panel-inner');
      const endH = measure(target);

      if (honorReduced && prefersReduced) {
        setHeight(panel, 'auto');
        return;
      }

      animateHeight(panel, endH, duration, opts.instant);
    }

    function close(btn, panel, opts = {}) {
      btn.setAttribute('aria-expanded', 'false');
      if (honorReduced && prefersReduced) {
        setHeight(panel, 0);
        return;
      }
      const current = panel.offsetHeight;
      animateHeight(panel, 0, duration, false, current);
    }

    function animateHeight(el, to, dur, instant, fromOverride, setAutoAfter = (to > 0)) {
        // GSAP
        if (window.gsap && !instant) {
            const fromH = typeof fromOverride === 'number' ? fromOverride : el.offsetHeight;
            if (to === 'auto') {
            window.gsap.set(el, { height: 'auto' });
            return;
            }
            window.gsap.to(el, {
            height: to,
            duration: dur,
            ease: getCssVar(section, '--mfc-ac-ease') || 'power2.out',
            onComplete: () => { if (setAutoAfter) setHeight(el, 'auto'); },
            onStart: () => { if (fromH === 0 && to > 0) setHeight(el, 0); }
            });
            return;
        }
        // Fallback rAF
        if (instant) { setHeight(el, to === 'auto' ? 'auto' : to); return; }

        const startH = el.offsetHeight;
        const endH = to === 'auto' ? measure(el.querySelector('.mfc-acc__panel-inner')) : to;
        const startT = performance.now();
        const ease = cubicBezierParser(getCssVar(section, '--mfc-ac-ease')) || (t=>t);
        function step(now) {
            const t = Math.min(1, (now - startT) / (dur * 1000));
            const v = ease(t);
            const h = startH + (endH - startH) * v;
            setHeight(el, h);
            if (t < 1) requestAnimationFrame(step);
            else if (setAutoAfter) setHeight(el, 'auto');
        }
        requestAnimationFrame(step);
        }

        // --- About "read more" toggle ---
const about = section.querySelector('.mfc-about');
if (about && about.getAttribute('data-collapsible') === 'true') {
  const panel = about.querySelector('.mfc-about__panel');
  const inner = about.querySelector('.mfc-about__inner');
  const toggle = about.querySelector('.mfc-about__toggle');
  const more = toggle?.querySelector('.mfc-about__more');
  const less = toggle?.querySelector('.mfc-about__less');
  const collapsedH = parseInt(getCssVar(section, '--mfc-about-collapsed')) || 160;

  // État initial replié
  setHeight(panel, collapsedH);
  about.setAttribute('data-expanded', 'false');
  
  toggle?.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    const dur = duration;

    if (expanded) {
      // on ferme vers la hauteur repliée -> ne PAS mettre auto à la fin
      toggle.setAttribute('aria-expanded', 'false');
      about.setAttribute('data-expanded', 'false');
      if (more) more.hidden = false;
      if (less) less.hidden = true;
      if (prefersReduced && honorReduced) { setHeight(panel, collapsedH); return; }
      animateHeight(panel, collapsedH, dur, false, panel.offsetHeight, /*setAutoAfter*/ false);
    } else {
      // on ouvre vers la hauteur du contenu -> auto à la fin
      toggle.setAttribute('aria-expanded', 'true');
      about.setAttribute('data-expanded', 'true');
      if (more) more.hidden = true;
      if (less) less.hidden = false;
      const endH = inner.scrollHeight + 1; // buffer to prevent cropping
        if (prefersReduced && honorReduced) { setHeight(panel, 'auto'); return; }
        animateHeight(panel, endH, dur, false, undefined, /*setAutoAfter*/ true);
    }
  });
}
  }

  // Utilities
  function setHeight(el, h) {
    el.style.height = typeof h === 'number' ? `${h}px` : h;
  }
  function measure(inner) {
    const prev = inner.parentElement.style.height;
    inner.parentElement.style.height = 'auto';
    const h = inner.parentElement.offsetHeight;
    inner.parentElement.style.height = prev || '0px';
    return h;
  }
  function getCssVar(el, name) {
    return getComputedStyle(el).getPropertyValue(name).trim();
  }
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  function safeParse(s) { try { return JSON.parse(s); } catch(e) { return {}; } }

  // Basic cubic-bezier parser → returns easing function or null
  function cubicBezierParser(str) {
    if (!str || !str.includes('cubic-bezier')) return null;
    const nums = str
      .replace(/cubic-bezier\(|\)|\s/g, '')
      .split(',')
      .map(parseFloat);
    if (nums.length !== 4 || nums.some(isNaN)) return null;
    const [p0x, p0y, p1x, p1y] = [0,0,1,1];
    const [x1, y1, x2, y2] = nums;
    // Approximation using Penner-like cubic
    return function (t) {
      // Solve x via Newton-Raphson to find param u s.t. BezierX(u)=t, then return BezierY(u)
      let u = t;
      for (let i = 0; i < 5; i++) {
        const x = bezier(u, p0x, x1, x2, p1x);
        const dx = bezierDer(u, p0x, x1, x2, p1x);
        if (dx === 0) break;
        u -= (x - t) / dx;
        u = Math.min(1, Math.max(0, u));
      }
      return bezier(u, p0y, y1, y2, p1y);
    };
    function bezier(u,a,b,c,d){
      const inv=1-u;
      return inv*inv*inv*a + 3*inv*inv*u*b + 3*inv*u*u*c + u*u*u*d;
    }
    function bezierDer(u,a,b,c,d){
      return 3*(1-u)*(1-u)*(b-a) + 6*(1-u)*u*(c-b) + 3*u*u*(d-c);
    }
  }

  // Auto-init
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.mfc-product-info').forEach(init);
  });
})();
